import { FC, useEffect, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Input, Label, Button, Password } from "../../common";
import { FormErrorMessage } from "../../messages";
import { ROUTES } from "../../../routes";
import { useLoginMutation } from "../../../store/services/auth";
import { selectContactLink } from "../../../store/reducers/contacts";

type Inputs = {
  email: string;
  password: string;
  contact_link_id?: string;
};

export const LoginForm: FC = () => {
  const contactLink = useSelector(selectContactLink);
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;

  const [formData, setFormData] = useState<Inputs>({
    email: contactLink?.email || "",
    password: "",
    contact_link_id: contactLink?.id,
  });

  const [login, { data, isSuccess, isLoading, isError, error }] =
    useLoginMutation();

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<Inputs>({
    defaultValues: {
      email: contactLink?.user_with_email_exists ? contactLink.email : "",
      password: "",
      contact_link_id: contactLink?.id,
    },
  });

  useEffect(() => {
    if (isSuccess && !data) {
      navigate(ROUTES.AUTH_VERIFY, {
        replace: true,
        state: {
          ...state,
          form: { ...formData },
        },
      });
    }
  }, [isLoading, isSuccess, navigate, data, formData, state]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setFormData(data);
    await login(data);
  };

  return (
    <div className="mt-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Label label="Email" className="mb-8">
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Input type="email" autoFocus {...field} />}
          />
        </Label>

        <Label label="Password" className="mb-12">
          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <Password {...field} />}
          />
        </Label>

        <FormErrorMessage className="mb-6" isError={isError} error={error} />

        <Button
          type="submit"
          data-test="login-btn"
          variant={Button.variant.PRIMARY}
          className="w-full mb-6"
          disabled={!isDirty || !isValid}
        >
          Log In
        </Button>

        <Button
          variant={Button.variant.TERTIARY}
          className="w-full"
          onClick={() => navigate(ROUTES.AUTH_RECOVERY)}
          data-test="forgot-password-btn"
        >
          Forgot password
        </Button>
      </form>
    </div>
  );
};
